/* Copyright (c) 2026 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useEffect, useMemo } from "react";
import dayjs from "dayjs";
import {
  EventsSortFields,
  RoleBindingsByUsersSortFields,
  SortOrder,
  useGetEventsViewQuery,
} from "~/generated/graphql";
import { useCustomer } from "~/context";
import { isNotEmpty } from "~/tools/size";
import { useGetRoleBindingsByUsers } from "~/views/permissions/Users/hooks";
import { useGetPlans } from "~/views/plans/hooks/useGetPlans/useGetPlans";
import { useDashboardAccounts } from "./useDashboardAccounts";

export function useTopSummaryData() {
  const { customer } = useCustomer();

  const {
    count: usersCount,
    refetch: refetchUsers,
    loading: usersLoading,
    error: usersError,
  } = useGetRoleBindingsByUsers({
    page: "1",
    limit: "1",
    sort: {
      field: RoleBindingsByUsersSortFields.Subject,
      order: SortOrder.Asc,
    },
  });

  const {
    count: plansCount,
    refetch: refetchPlans,
    loading: plansLoading,
    error: plansError,
  } = useGetPlans({
    page: "1",
    limit: "1",
    basic: true,
  });

  const {
    data: eventsData,
    refetch: refetchEvents,
    loading: eventsLoading,
  } = useGetEventsViewQuery({
    variables: {
      page: 1,
      limit: 1,
      sort: {
        field: EventsSortFields.StartTime,
        order: SortOrder.Asc,
      },
    },
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });

  const {
    getCloudAccounts,
    accountsStatus,
    count: accountsCount,
    loading: accountsLoading,
  } = useDashboardAccounts();

  const averageMonthlyEvents = useMemo(() => {
    if (
      eventsData?.eventsView?.result &&
      isNotEmpty(eventsData?.eventsView?.result ?? [])
    ) {
      const monthCount = dayjs().diff(
        dayjs(eventsData?.eventsView?.result[0].startTime),
        "month"
      );
      return Math.floor(
        eventsData?.eventsView.pageInfo.count /
          (monthCount === 0 ? 1 : monthCount)
      );
    }

    return eventsData?.eventsView.pageInfo.count;
  }, [eventsData]);

  useEffect(() => {
    refetchPlans();
    refetchUsers();
    refetchEvents();
    getCloudAccounts();
  }, [customer, refetchPlans, refetchUsers, refetchEvents, getCloudAccounts]);

  return {
    usersCount: usersError ? undefined : usersCount,
    plansCount: plansError ? undefined : plansCount,
    averageMonthlyEvents,
    usersLoading,
    plansLoading,
    eventsLoading,
    accountsCount,
    accountsLoading,
    accountsStatus,
  } as const;
}
